import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from "chart.js";
import { getToken } from "../utils/auth";
import "./MCQReportPageStaff.css";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function MCQReportPageStaff() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { mcqId, college } = state || {};

  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ---------- FILTER STATES ---------- */
  const [searchText, setSearchText] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("ALL");
  const [showAnalysis, setShowAnalysis] = useState(false);

  /* ---------- FETCH REPORT ---------- */
  useEffect(() => {
    if (!mcqId || !college) {
      navigate(-1);
      return;
    }

    async function fetchReport() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/mcq-submissions/getSubmissionByCollegeAndMcq`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`
            },
            body: JSON.stringify({ mcqId, college })
          }
        );

        const data = await res.json();
        setReport(data || []);
      } catch (err) {
        console.error("Failed to fetch MCQ report", err);
      } finally {
        setLoading(false);
      }
    }

    fetchReport();
  }, [mcqId, college, navigate]);

  /* ---------- UNIQUE BATCHES ---------- */
  const batches = useMemo(() => {
    const set = new Set(report.map(r => r.batch));
    return ["ALL", ...Array.from(set)];
  }, [report]);

  /* ---------- FILTERED DATA ---------- */
  const filteredReport = useMemo(() => {
    return report.filter(r => {
      const batchMatch =
        selectedBatch === "ALL" || r.batch === selectedBatch;
        
      const nameMatch =
        r.studentId?.name
          ?.toLowerCase()
          .includes(searchText.toLowerCase()) ||
        r.studentId?.email
          ?.toLowerCase()
          .includes(searchText.toLowerCase());

      return batchMatch && nameMatch;
    });
  }, [report, selectedBatch, searchText]);

  /* ---------- SUMMARY ---------- */
  const summary = useMemo(() => {
    const total = filteredReport.length;
    const avgScore =
      total === 0
        ? 0
        : (
            filteredReport.reduce((s, r) => s + (r.score || 0), 0) / total
          ).toFixed(1);

    const tabSwitchCount = filteredReport.filter(r => r.isTabSwitch).length;

    return { total, avgScore, tabSwitchCount };
  }, [filteredReport]);

  /* ---------- PIE CHART ---------- */
  const pieData = useMemo(() => {
    const buckets = { low: 0, mid: 0, high: 0 };

    filteredReport.forEach(r => {
      const percent = r.totalMarks > 0 ? (r.score / r.totalMarks) * 100 : 0;
      if (percent < 40) buckets.low++;
      else if (percent <= 70) buckets.mid++;
      else buckets.high++;
    });

    return {
      labels: ["Under 40%", "40% – 70%", "Above 70%"],
      datasets: [
        {
          data: [buckets.low, buckets.mid, buckets.high],
          backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
          hoverOffset: 4,
        }
      ]
    };
  }, [filteredReport]);

  /* ---------- DOWNLOAD ---------- */
  const downloadExcel = () => {
    const rows = filteredReport.map(r => ({
      Name: r.studentId?.name,
      Email: r.studentId?.email,
      Batch: r.batch,
      Year: r.year,
      College: r.college,
      Score: r.score,
      TotalMarks: r.totalMarks,
      TabSwitch: r.isTabSwitch ? "Yes" : "No",
      SubmittedAt: new Date(r.submittedAt).toLocaleString()
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MCQ Report");
    XLSX.writeFile(wb, `MCQ_Report_${college || "Staff"}.xlsx`);
  };

  return (
    <div className="mcq-report-container">
      {/* HEADER */}
      <header className="report-header">
        <div>
          <h2>MCQ Performance Report</h2>
          <p className="subtitle">College-wise metrics and detailed multi-choice test insights</p>
        </div>
        <button className="btn-secondary" onClick={() => setShowAnalysis(p => !p)}>
          {showAnalysis ? "🗙 Hide Analytics" : "📊 View Analytics"}
        </button>
      </header>

      {/* SUMMARY CARDS */}
      <section className="summary-grid">
        <div className="summary-card">
          <div className="card-icon info-icon">👥</div>
          <div>
            <span className="card-label">Total Candidates</span>
            <h3 className="card-value">{summary.total}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon success-icon">🎯</div>
          <div>
            <span className="card-label">Average Score</span>
            <h3 className="card-value">{summary.avgScore}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon alert-icon">⚠️</div>
          <div>
            <span className="card-label">Tab Switches flagged</span>
            <h3 className="card-value alert-text">{summary.tabSwitchCount}</h3>
          </div>
        </div>
      </section>

      {/* ANALYSIS SECTION */}
      {showAnalysis && (
        <section className="analysis-card-section animate-fade">
          <div className="analysis-header">
            <h3>Score Distribution Overview</h3>
          </div>
          <div className="chart-wrapper">
            <Pie 
              data={pieData} 
              options={{ responsive: true, maintainAspectRatio: false }} 
            />
          </div>
        </section>
      )}

      {/* FILTER BAR */}
      <section className="filter-toolbar">
        <div className="search-group">
          <div className="input-with-icon">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search name or email..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
          
          <div className="select-wrapper">
            <select
              value={selectedBatch}
              onChange={e => setSelectedBatch(e.target.value)}
            >
              {batches.map(b => (
                <option key={b} value={b}>{b === "ALL" ? "All Batches" : `Batch ${b}`}</option>
              ))}
            </select>
          </div>
        </div>

        <button className="btn-primary" onClick={downloadExcel}>
          📥 Export Excel
        </button>
      </section>

      {/* STATUS UI STATES */}
      {loading && (
        <div className="state-container">
          <div className="spinner"></div>
          <p>Processing metrics report data...</p>
        </div>
      )}
      
      {!loading && filteredReport.length === 0 && (
        <div className="state-container empty-state">
          <p>No submission entries match your search criteria.</p>
        </div>
      )}

      {/* TABLE */}
      {!loading && filteredReport.length > 0 && (
        <div className="table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Student Details</th>
                <th>Academic Year</th>
                <th>Batch Allocation</th>
                <th className="text-center">Score Metric</th>
                <th className="text-center">Security Check</th>
                <th>Submission Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {filteredReport.map((r, i) => (
                <tr key={i}>
                  <td>
                    <div className="student-profile">
                      <span className="student-name">{r.studentId?.name || "N/A"}</span>
                      <span className="student-email">{r.studentId?.email || "-"}</span>
                    </div>
                  </td>
                  <td><span className="badge-neutral">Year {r.year}</span></td>
                  <td><span className="badge-neutral">{r.batch}</span></td>
                  <td className="text-center font-semibold">
                    <span className="score-display">{r.score}</span>
                    <span className="total-display"> / {r.totalMarks}</span>
                  </td>
                  <td className="text-center">
                    <span className={`status-pill ${r.isTabSwitch ? "status-failed" : "status-passed"}`}>
                      {r.isTabSwitch ? "Flagged Switch" : "Passed"}
                    </span>
                  </td>
                  <td className="timestamp-cell">
                    {new Date(r.submittedAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}